import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { ExternalLink, Eye, Heart, MessageCircle, ThumbsUp } from "lucide-react";
import { getAllTeachersList, getExamListNew, getParentCourseNew, getYTAnalytics } from "../../Action/courseAction";
import { CustomAdminTable, CustomBadge, CustomIconButton, CustomPageLayout } from "../../Component/customComponents";
import { TableTitle } from "../Common/transactionCommon";
import { exportRowsToExcel } from "../Common/countListCommon";

const yesterday = () => moment().subtract(1, "days").format("YYYY-MM-DD");
const today = () => moment().format("YYYY-MM-DD");

/** Request body for getYTAnalytics (same keys as before) */
const buildReqBody = (f) => ({
  startDate: f.startDate,
  endDate: f.endDate,
  ...(f.exam && { examTypeId: f.exam._id }),
  ...(f.examStream && { baseCourseId: f.examStream._id }),
  ...(f.teacher && { teacherId: f.teacher._id }),
});

const num = (v) => Number(v) || 0;
const istDay = (v) => (v ? moment.utc(v).utcOffset(330).format("DD/MM/YYYY") : "—"); // same IST shift as the old table

const TeacherPerformance = () => {
  const dispatch = useDispatch();
  const { role, userInfo } = useSelector((state) => ({ role: state?.Auth?.userInfo?.role, userInfo: state?.Auth?.userInfo }));
  const isTeacher = role === "TEACHER";

  const [filters, setFilters] = useState({ startDate: yesterday(), endDate: yesterday(), exam: "", examStream: "", teacher: "" });
  const [lockedTeacher, setLockedTeacher] = useState(""); // TEACHER role: their own record, never changed
  const [examList, setExamList] = useState([]);
  const [examStreamList, setExamStreamList] = useState([]);
  const [teacherList, setTeacherList] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchPerformance = (f) => {
    setLoading(true);
    dispatch(
      getYTAnalytics(
        buildReqBody(f),
        (resp) => {
          // only YouTube live lectures, as before
          setRows((resp?.data || []).filter((item) => item?.taskType === "YOUTUBE_LIVE"));
          setPage(1);
          setLoading(false);
        },
        () => {
          setRows([]);
          setLoading(false);
        },
      ),
    );
  };

  const loadStreams = (exam) => {
    setExamStreamList([]);
    if (exam) dispatch(getParentCourseNew(`${exam._id}`, (res) => setExamStreamList(res || [])));
  };

  useEffect(() => {
    dispatch(getExamListNew((res) => setExamList(res || [])));
    dispatch(
      getAllTeachersList(
        { skip: 0, limit: 100 },
        (res) => {
          const list = res?.data?.data || [];
          setTeacherList(list);
          if (!isTeacher) return;
          // logged-in teacher: lock the filter to their own record (matched by admin id or username)
          const own = list.find(
            (item) =>
              (userInfo?._id && item?.adminId?._id === userInfo?._id) ||
              (userInfo?.username && item?.adminId?.username === userInfo?.username),
          );
          const next = { ...filters, teacher: own || "" };
          setLockedTeacher(own || "");
          setFilters(next);
          fetchPerformance(next);
        },
        () => isTeacher && fetchPerformance(filters),
      ),
    );
    if (!isTeacher) fetchPerformance(filters);
  }, []);

  const filterFields = useMemo(
    () => [
      { key: "startDate", label: "Start Date", type: "date", half: true, isRequired: true, defaultValue: yesterday, maxDate: today },
      {
        key: "endDate",
        label: "End Date",
        type: "date",
        half: true,
        isRequired: true,
        defaultValue: yesterday,
        minDate: (v) => v.startDate,
        // at most one month after the start date, as before
        maxDate: (v) => (v.startDate ? moment(v.startDate).add(1, "months").format("YYYY-MM-DD") : undefined),
        validate: (value, v) =>
          value && v.startDate && value < v.startDate
            ? "End date can't be before the start date"
            : value && v.startDate && value > moment(v.startDate).add(1, "months").format("YYYY-MM-DD")
              ? "Pick at most one month"
              : "",
      },
      { key: "exam", label: "Exam Name", type: "dropdown", options: examList, clears: isTeacher ? ["examStream"] : ["examStream", "teacher"] },
      {
        key: "examStream",
        label: "Exam Stream",
        type: "dropdown",
        options: examStreamList,
        clears: isTeacher ? [] : ["teacher"],
        disabled: (v) => !v.exam,
        hint: (v) => (!v.exam ? "Select an exam first" : undefined),
      },
      {
        key: "teacher",
        label: "Teacher Name",
        type: "dropdown",
        options: teacherList,
        defaultValue: () => lockedTeacher,
        disabled: isTeacher && Boolean(lockedTeacher),
        hint: isTeacher && lockedTeacher ? "Your own performance" : undefined,
        countAsFilter: !(isTeacher && lockedTeacher),
      },
    ],
    [examList, examStreamList, teacherList, isTeacher, lockedTeacher],
  );

  const totals = useMemo(
    () =>
      rows.reduce(
        (t, r) => ({
          views: t.views + num(r?.viewCount),
          likes: t.likes + num(r?.likeCount),
          comments: t.comments + num(r?.commentCount),
          favorites: t.favorites + num(r?.favoriteCount),
        }),
        { views: 0, likes: 0, comments: 0, favorites: 0 },
      ),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      [r?.teacherId?.name, r?.examTypeId?.name, r?.baseCourseId?.name, r?.link].filter(Boolean).some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [rows, searchText]);
  const pagedRows = useMemo(() => filteredRows.slice((page - 1) * pageSize, page * pageSize), [filteredRows, page, pageSize]);

  const stat = (v) => (v ? num(v).toLocaleString("en-IN") : "—");
  const columns = [
    { key: "taskDate", header: "Date", minWidth: 110, render: (v) => istDay(v) },
    { key: "taskType", header: "Lecture Type", minWidth: 130, render: (v) => (v ? <CustomBadge tone="danger" size="sm">{v}</CustomBadge> : "—") },
    { key: "exam", header: "Exam", minWidth: 120, render: (_, r) => r?.examTypeId?.name || "—" },
    { key: "department", header: "Department", minWidth: 130, render: (_, r) => r?.baseCourseId?.name || "—" },
    { key: "teacher", header: "Teacher Name", minWidth: 150, render: (_, r) => <strong>{r?.teacherId?.name || "—"}</strong> },
    {
      key: "link",
      header: "YT Link",
      align: "center",
      render: (v) => (v ? <CustomIconButton icon={<ExternalLink size={14} />} size="sm" title="Open on YouTube" href={v} target="_blank" /> : "—"),
    },
    { key: "youtubeDuration", header: "Duration", render: (v) => v || "—" },
    { key: "viewCount", header: "Views", align: "right", render: stat },
    { key: "likeCount", header: "Like", align: "right", render: stat },
    { key: "commentCount", header: "Comment", align: "right", render: stat },
    { key: "favoriteCount", header: "Favorite", align: "right", render: stat },
  ];

  const onExport = () =>
    exportRowsToExcel(
      "Teacher Task List",
      "Teacher Task List",
      rows.map((r, i) => ({
        "S.N": i + 1,
        DATE: r?.taskDate ? moment.utc(r.taskDate).utcOffset(330).format("DD-MM-YYYY") : "-",
        "LECTURE TYPE": r?.taskType || "-",
        EXAM: r?.examTypeId?.name || "-",
        DEPARTMENT: r?.baseCourseId?.name || "-",
        "TEACHER NAME": r?.teacherId?.name || "-",
        "YT LINK": r?.link || "-",
        DURATION: r?.youtubeDuration || "-",
        VIEWS: r?.viewCount || "-",
        LIKE: r?.likeCount || "-",
        COMMENT: r?.commentCount || "-",
        FAVORITE: r?.favoriteCount || "-",
      })),
    );

  return (
    <CustomPageLayout title="TEACHER PERFORMANCE">
      <CustomAdminTable
        id="teacher-performance-table"
        title={<TableTitle title={`YouTube Live Lectures (${rows.length})`} from={filters.startDate} to={filters.endDate} />}
        columns={columns}
        data={pagedRows}
        loading={loading}
        rowKey={(row, index) => row?._id || index}
        enableSerialNumber
        serialHeader="S.No"
        search={{
          value: searchText,
          onChange: (v) => {
            setSearchText(v);
            setPage(1);
          },
          placeholder: "Search teacher, exam or link...",
        }}
        toolbarActions={
          <>
            <CustomBadge tone="primary" icon={<Eye size={12} />}>{totals.views.toLocaleString("en-IN")}</CustomBadge>
            <CustomBadge tone="success" icon={<ThumbsUp size={12} />}>{totals.likes.toLocaleString("en-IN")}</CustomBadge>
            <CustomBadge tone="info" icon={<MessageCircle size={12} />}>{totals.comments.toLocaleString("en-IN")}</CustomBadge>
            <CustomBadge tone="danger" icon={<Heart size={12} />}>{totals.favorites.toLocaleString("en-IN")}</CustomBadge>
          </>
        }
        exportButton={rows.length > 0}
        onExport={onExport}
        advancedFilters={filterFields}
        advancedFilterValues={filters}
        onApplyFilters={(values) => {
          const next = isTeacher && lockedTeacher ? { ...values, teacher: lockedTeacher } : values;
          setFilters(next);
          fetchPerformance(next);
        }}
        applyFiltersOnReset
        onFilterChange={(key, value) => key === "exam" && loadStreams(value)}
        onFilterDrawerOpen={(applied) => loadStreams(applied.exam)}
        filterSubtitle="Pick the dates (up to one month), exam, stream or teacher"
        pagination={{
          page,
          pageSize,
          total: filteredRows.length,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(1);
          },
        }}
        emptyTitle={searchText ? "No matches" : "No YouTube live lectures"}
        emptyDescription={searchText ? "Try a different search." : "No YouTube live lectures for these filters."}
        maxHeight="83vh"
      />
    </CustomPageLayout>
  );
};

export default TeacherPerformance;
